// ============================================================
// Inventive Pages — PHP + MySQL API Service
// ============================================================

/**
 * Get the API base URL from runtime config (config.js)
 * or fall back to auto-detection.
 * Edit /public/config.js to change the URL without rebuilding.
 */
function getApiBaseUrl() {
  let base = (window.APP_CONFIG && window.APP_CONFIG.apiUrl)
    ? window.APP_CONFIG.apiUrl
    : '/api/';
  // Ensure no trailing slash
  base = base.replace(/\/$/, '');
  // If it's an absolute path (starts with /), prepend origin to make a full URL
  if (base.startsWith('/')) {
    base = window.location.origin + base;
  }
  return base;
}

class PhpAPI {
  constructor() {
    // Base URL is resolved lazily so window.APP_CONFIG is already set
    this._base = null;
  }

  get base() {
    if (!this._base) this._base = getApiBaseUrl();
    return this._base;
  }

  /**
   * All calls use GET with ?action=xxx&data=JSON
   */
  async request(action, params = {}, postData = null) {
    try {
      const url = new URL(this.base + '/index.php');
      url.searchParams.set('action', action);

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.set(key, String(value));
        }
      });

      if (postData) {
        url.searchParams.set('data', JSON.stringify(postData));
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        redirect: 'follow',
        cache: 'no-store',
      });

      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse API response:', text);
        return { error: 'Invalid response from server' };
      }
    } catch (error) {
      console.error('API request failed:', error);
      return { error: error.message || 'Network error' };
    }
  }

  // ── OTP ──────────────────────────────────────────────────
  async sendOtp(phone, countryCode) {
    return this.request('sendOtp', {}, { phone, country_code: countryCode });
  }

  async verifyOtp(transactionId, otp) {
    return this.request('verifyOtp', {}, { transaction_id: transactionId, otp });
  }

  // ── Public ───────────────────────────────────────────────
  async createSubmission(data) {
    return this.request('createSubmission', {}, data);
  }

  async getPdfToken(submissionId) {
    return this.request('getPdfToken', { submission_id: submissionId });
  }

  async getSubmissionByToken(token) {
    return this.request('getSubmissionByToken', { token });
  }

  async getFormOptions() {
    return this.request('getFormOptions');
  }

  async getCountryCodes() {
    return this.request('getCountryCodes');
  }

  // ── Auth ─────────────────────────────────────────────────
  async login(email, password) {
    return this.request('login', {}, { email, password });
  }

  async getMe(token) {
    return this.request('getMe', { token });
  }

  // ── Admin Submissions ────────────────────────────────────
  async getSubmissions(params = {}, token) {
    return this.request('getSubmissions', { ...params, token });
  }

  async getSubmission(id, token) {
    return this.request('getSubmission', { id, token });
  }

  async updateSubmissionStatus(id, status, token) {
    return this.request('updateSubmissionStatus', { token }, { id, status });
  }

  async updateSubmission(data, token) {
    return this.request('updateSubmission', { token }, data);
  }

  async deleteSubmission(id, token) {
    return this.request('deleteSubmission', { id, token });
  }

  async deleteSubmissions(ids, token) {
    return this.request('deleteSubmissions', { token }, { ids });
  }

  // ── Notes ────────────────────────────────────────────────
  async addNote(submissionId, noteText, token) {
    return this.request('addNote', { token }, { submission_id: submissionId, note_text: noteText });
  }

  async getNotes(submissionId, token) {
    return this.request('getNotes', { submission_id: submissionId, token });
  }

  // ── User Management ──────────────────────────────────────
  async getUsers(token) {
    return this.request('getUsers', { token });
  }

  async createUser(data, token) {
    return this.request('createUser', { token }, data);
  }

  async deleteUser(userId, token) {
    return this.request('deleteUser', { user_id: userId, token });
  }

  // ── Settings ─────────────────────────────────────────────
  async getSettings(token) {
    return this.request('getSettings', { token });
  }

  async saveSettings(data, token) {
    return this.request('saveSettings', { token }, data);
  }

  // ── Dashboard ────────────────────────────────────────────
  async getDashboardStats(token) {
    return this.request('getDashboardStats', { token });
  }

  // ── Export ───────────────────────────────────────────────
  async exportData(token) {
    return this.request('exportData', { token });
  }

  // ── Nuptial Events ───────────────────────────────────────
  async getNuptialEvents(token) {
    return this.request('getNuptialEvents', { token });
  }

  async addNuptialEvent(data, token) {
    return this.request('addNuptialEvent', { token }, data);
  }

  async updateNuptialEvent(data, token) {
    return this.request('updateNuptialEvent', { token }, data);
  }

  async deleteNuptialEvent(eventId, token) {
    return this.request('deleteNuptialEvent', { event_id: eventId, token });
  }

  // ── Themes ───────────────────────────────────────────────
  async getThemes(token) {
    return this.request('getThemes', { token });
  }

  async addTheme(data, token) {
    return this.request('addTheme', { token }, data);
  }

  async updateTheme(data, token) {
    return this.request('updateTheme', { token }, data);
  }

  async deleteTheme(themeId, token) {
    return this.request('deleteTheme', { theme_id: themeId, token });
  }

  // ── Theme Categories ─────────────────────────────────────
  async getThemeCategories(token) {
    return this.request('getThemeCategories', { token });
  }

  async addThemeCategory(data, token) {
    return this.request('addThemeCategory', { token }, data);
  }

  async updateThemeCategory(data, token) {
    return this.request('updateThemeCategory', { token }, data);
  }

  async deleteThemeCategory(categoryId, token) {
    return this.request('deleteThemeCategory', { category_id: categoryId, token });
  }

  // ── Cities ───────────────────────────────────────────────
  async getCities(token) {
    return this.request('getCities', { token });
  }

  async addCity(data, token) {
    return this.request('addCity', { token }, data);
  }

  async updateCity(data, token) {
    return this.request('updateCity', { token }, data);
  }

  async deleteCity(cityId, token) {
    return this.request('deleteCity', { city_id: cityId, token });
  }

  // ── Main Services ────────────────────────────────────────
  async getMainServices(token) {
    return this.request('getMainServices', { token });
  }

  async addMainService(data, token) {
    return this.request('addMainService', { token }, data);
  }

  async updateMainService(data, token) {
    return this.request('updateMainService', { token }, data);
  }

  async deleteMainService(serviceId, token) {
    return this.request('deleteMainService', { service_id: serviceId, token });
  }

  // ── Service Tags ─────────────────────────────────────────
  async getServiceTags(serviceId, token) {
    return this.request('getServiceTags', { service_id: serviceId, token });
  }

  async addServiceTag(data, token) {
    return this.request('addServiceTag', { token }, data);
  }

  async updateServiceTag(data, token) {
    return this.request('updateServiceTag', { token }, data);
  }

  async deleteServiceTag(tagId, token) {
    return this.request('deleteServiceTag', { tag_id: tagId, token });
  }

  // ── Legacy aliases (for compatibility) ───────────────────
  async getEvents(token)                    { return this.getNuptialEvents(token); }
  async addEvent(data, token)               { return this.addNuptialEvent(data, token); }
  async updateEvent(data, token)            { return this.updateNuptialEvent(data, token); }
  async deleteEvent(eventId, token)         { return this.deleteNuptialEvent(eventId, token); }
  async getDestinations(token)              { return this.getCities(token); }
  async addDestination(data, token)         { return this.addCity(data, token); }
  async updateDestination(data, token)      { return this.updateCity(data, token); }
  async deleteDestination(destId, token)    { return this.deleteCity(destId, token); }
  async getServices(token)                  { return this.getMainServices(token); }
  async addService(data, token)             { return this.addMainService(data, token); }
  async updateService(data, token)          { return this.updateMainService(data, token); }
  async deleteService(serviceId, token)     { return this.deleteMainService(serviceId, token); }

  // ── Profile ──────────────────────────────────────────────
  async updateProfile(data, token) {
    return this.request('updateProfile', { token }, data);
  }

  async changePassword(data, token) {
    return this.request('changePassword', { token }, data);
  }

  // ── Landing Page Content ─────────────────────────────────
  async getLandingContent(token) {
    return this.request('getLandingContent', token ? { token } : {});
  }

  async saveLandingContent(data, token) {
    return this.request('saveLandingContent', { token }, data);
  }

  // ── Testimonials ─────────────────────────────────────────
  async getTestimonials(token) {
    return this.request('getTestimonials', token ? { token } : {});
  }

  async addTestimonial(data, token) {
    return this.request('addTestimonial', { token }, data);
  }

  async updateTestimonial(data, token) {
    return this.request('updateTestimonial', { token }, data);
  }

  async deleteTestimonial(testimonialId, token) {
    return this.request('deleteTestimonial', { testimonial_id: testimonialId, token });
  }

  // ── Awards & Recognitions ────────────────────────────────
  async getAwards(token) {
    return this.request('getAwards', token ? { token } : {});
  }

  async addAward(data, token) {
    return this.request('addAward', { token }, data);
  }

  async updateAward(data, token) {
    return this.request('updateAward', { token }, data);
  }

  async deleteAward(awardId, token) {
    return this.request('deleteAward', { award_id: awardId, token });
  }

  async getFooterLogos(token) {
    return this.request('getFooterLogos', token ? { token } : {});
  }

  async addFooterLogo(data, token) {
    return this.request('addFooterLogo', { token }, data);
  }

  async updateFooterLogo(data, token) {
    return this.request('updateFooterLogo', { token }, data);
  }

  async deleteFooterLogo(logoId, token) {
    return this.request('deleteFooterLogo', { footer_logo_id: logoId, token });
  }

  // ── Landing Reels (videos) ───────────────────────────────
  async getReels(token) {
    return this.request('getReels', token ? { token } : {});
  }

  async addReel(data, token) {
    return this.request('addReel', { token }, data);
  }

  async updateReel(data, token) {
    return this.request('updateReel', { token }, data);
  }

  async deleteReel(reelId, token) {
    return this.request('deleteReel', { reel_id: reelId, token });
  }

  // ── Gallery Shorts ───────────────────────────────────────
  async getGalleryShorts(token) {
    return this.request('getGalleryShorts', token ? { token } : {});
  }

  async addGalleryShort(data, token) {
    return this.request('addGalleryShort', { token }, data);
  }

  async updateGalleryShort(data, token) {
    return this.request('updateGalleryShort', { token }, data);
  }

  async deleteGalleryShort(shortId, token) {
    return this.request('deleteGalleryShort', { short_id: shortId, token });
  }

  // ── Health ───────────────────────────────────────────────
  async health() {
    return this.request('health');
  }

  /**
   * Upload landing image (founders, awards, footer). Requires super_admin token.
   * Returns { success, url } on success.
   */
  async uploadLandingImage(file, token) {
    const form = new FormData();
    form.append('file', file);
    const url = new URL(this.base + '/index.php');
    url.searchParams.set('action', 'uploadLandingImage');
    if (token) url.searchParams.set('token', token);
    try {
      const response = await fetch(url.toString(), { method: 'POST', body: form });
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        return { error: text || 'Invalid response' };
      }
    } catch (error) {
      return { error: error.message || 'Network error' };
    }
  }
}

export const api = new PhpAPI();
export default api;
